import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, Observable, switchMap, take, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { CsrfService, CsrfTokenResponse } from '../services/csrf-service';

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE']);
const CSRF_TOKEN_INVALID_ERROR_CODE = 'ERR_0016';
const CSRF_MANAGED_REQUEST = new HttpContextToken<boolean>(() => false);
const CSRF_RETRY_ATTEMPTED = new HttpContextToken<boolean>(() => false);

const isInvalidCsrfTokenError = (error: HttpErrorResponse): boolean => {
  const responseBody = error.error;
  return error.status === 403 &&
    typeof responseBody === 'object' &&
    responseBody !== null &&
    (responseBody as { errorCode?: unknown }).errorCode === CSRF_TOKEN_INVALID_ERROR_CODE;
};

export const shouldDeferCsrfError = (
  request: HttpRequest<unknown>,
  error: HttpErrorResponse,
): boolean =>
  request.context.get(CSRF_MANAGED_REQUEST) &&
  !request.context.get(CSRF_RETRY_ATTEMPTED) &&
  isInvalidCsrfTokenError(error);

const apiBaseUrl = environment.ENV !== 'local' ? environment.BASE_URL.replace(/\/+$/g, '') : '';
const backendApiUrl = new URL(apiBaseUrl || window.location.origin, window.location.origin);
const backendApiPath = backendApiUrl.pathname.replace(/\/+$/g, '') || '/';

const withCsrfToken = (
  requestWithCredentials: HttpRequest<unknown>,
  csrfToken: CsrfTokenResponse,
  retryAttempted: boolean,
): HttpRequest<unknown> =>
  requestWithCredentials.clone({
    context: requestWithCredentials.context
      .set(CSRF_MANAGED_REQUEST, true)
      .set(CSRF_RETRY_ATTEMPTED, retryAttempted),
    setHeaders: {
      [csrfToken.headerName]: csrfToken.token,
    },
  });

const handleRefreshTokenError = (
  csrfService: CsrfService,
  refreshError: HttpErrorResponse,
): Observable<never> => {
  if (refreshError.status === 401) {
    csrfService.clearToken();
  }
  return throwError(() => refreshError);
};

const retryWithFreshCsrfToken = (
  csrfService: CsrfService,
  requestWithCredentials: HttpRequest<unknown>,
  next: HttpHandlerFn,
  staleToken: CsrfTokenResponse,
): Observable<HttpEvent<unknown>> =>
  csrfService.refreshToken(staleToken.token).pipe(
    take(1),
    switchMap(refreshedToken => next(withCsrfToken(requestWithCredentials, refreshedToken, true))),
    catchError((refreshError: HttpErrorResponse) => handleRefreshTokenError(csrfService, refreshError)),
  );

const handleCsrfTokenError = (
  csrfService: CsrfService,
  requestWithCredentials: HttpRequest<unknown>,
  next: HttpHandlerFn,
  csrfToken: CsrfTokenResponse,
  error: HttpErrorResponse,
): Observable<HttpEvent<unknown>> => {
  if (!isInvalidCsrfTokenError(error) || requestWithCredentials.context.get(CSRF_RETRY_ATTEMPTED)) {
    return throwError(() => error);
  }

  return retryWithFreshCsrfToken(csrfService, requestWithCredentials, next, csrfToken);
};

export const csrfInterceptor: HttpInterceptorFn = (request, next) => {
  const requestUrl = new URL(request.url, document.baseURI);
  const requestPath = requestUrl.pathname.replace(/\/+$/g, '') || '/';
  const targetsBackendApi =
    requestUrl.origin === backendApiUrl.origin &&
    (backendApiPath === '/' ||
      requestPath === backendApiPath ||
      requestPath.startsWith(`${backendApiPath}/`));

  if (!targetsBackendApi) {
    return next(request);
  }

  const requestWithCredentials = request.clone({
    withCredentials: true,
  });

  if (SAFE_METHODS.has(request.method.toUpperCase())) {
    return next(requestWithCredentials);
  }

  const csrfService = inject(CsrfService);

  return csrfService.getToken().pipe(
    take(1),
    switchMap(csrfToken => next(withCsrfToken(requestWithCredentials, csrfToken, false)).pipe(
      catchError((error: HttpErrorResponse) =>
        handleCsrfTokenError(csrfService, requestWithCredentials, next, csrfToken, error)),
    )),
  );
};